import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3, PieChart, ClipboardList, Download, TrendingUp, Building2,
  Users, GraduationCap, Award, ShieldCheck, ArrowRight, FileSpreadsheet,
  Layers, Activity, Filter, AlertTriangle,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// =========================================================================
// SUPER ADMIN ANALYTICS DASHBOARD
// =========================================================================
export function AdminAnalyticsDashboardPage() {
  const [totals, setTotals] = useState(null);
  const [orgRows, setOrgRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d, o] = await Promise.all([
          api.get("/admin/analytics/dashboard"),
          api.get("/admin/analytics/organization-wise"),
        ]);
        setTotals(d.data.data.totals);
        setOrgRows(o.data.data.rows);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading || !totals) return <DashboardLayout title="Platform Analytics"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Platform Analytics" breadcrumbs={["Super Admin", "Analytics"]}>
      <div className="space-y-6" data-testid="admin-analytics">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Organizations" value={`${totals.active_organizations}/${totals.total_organizations}`} icon="Building2" tone="navy" testId="a-kpi-orgs" index={0} />
          <StatCard label="Learners" value={`${totals.active_learners}/${totals.total_learners}`} icon="Users" tone="green" testId="a-kpi-learners" index={1} />
          <StatCard label="Courses" value={`${totals.published_courses}/${totals.total_courses}`} icon="GraduationCap" tone="navy" testId="a-kpi-courses" index={2} />
          <StatCard label="Assignments" value={totals.total_assignments} icon="ClipboardList" tone="slate" testId="a-kpi-assign" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Completed" value={totals.completed} icon="CircleCheckBig" tone="green" testId="a-kpi-completed" index={4} />
          <StatCard label="In progress" value={totals.in_progress} icon="Activity" tone="amber" testId="a-kpi-inprog" index={5} />
          <StatCard label="Exam attempts" value={totals.total_exam_attempts} icon="FileCheck" tone="navy" testId="a-kpi-attempts" index={6} />
          <StatCard label="Pass %" value={`${totals.pass_percentage}%`} icon="TrendingUp" tone="green" testId="a-kpi-pass" index={7} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Certificates" value={totals.total_certificates} icon="Award" tone="navy" testId="a-kpi-certs" index={8} />
          <StatCard label="Valid" value={totals.valid_certificates} icon="ShieldCheck" tone="green" testId="a-kpi-valid" index={9} />
          <StatCard label="Expiring soon" value={totals.expiring_soon} icon="Clock" tone="amber" testId="a-kpi-expiring" index={10} />
          <StatCard label="Compliance %" value={`${totals.compliance_percentage}%`} icon="ShieldCheck" tone="navy" testId="a-kpi-compliance" index={11} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="org-wise-table">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-bold">Organization-wise performance</div>
            <button onClick={async () => {
              try {
                const { data } = await api.get("/admin/reports/course-exam-progress", { params: { format: "xlsx" }, responseType: "blob" });
                const url = URL.createObjectURL(new Blob([data]));
                const a = document.createElement("a");
                a.href = url; a.download = "course_exam_progress.xlsx"; a.click();
                URL.revokeObjectURL(url);
              } catch (err) { toast.error(formatApiError(err)); }
            }} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="btn-export-course-exam-progress">
              Export course-exam-progress XLSX
            </button>
          </div>
          {orgRows.length === 0 ? <EmptyState title="No orgs" description="No organizations yet." icon={Building2} /> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Organization</th>
                  <th className="px-4 py-3 text-left">Learners</th>
                  <th className="px-4 py-3 text-left">Assignments</th>
                  <th className="px-4 py-3 text-left">Completed</th>
                  <th className="px-4 py-3 text-left">Completion %</th>
                  <th className="px-4 py-3 text-left">Certificates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orgRows.map((o) => (
                  <tr key={o.id} data-testid={`org-row-${o.id}`}>
                    <td className="px-4 py-2 font-medium">{o.organization_name}<div className="font-mono text-xs text-slate-500">{o.organization_code}</div></td>
                    <td className="px-4 py-2 text-xs">{o.learners}</td>
                    <td className="px-4 py-2 text-xs">{o.assignments}</td>
                    <td className="px-4 py-2 text-xs">{o.completed}</td>
                    <td className="px-4 py-2 text-xs font-bold">{o.completion_percentage}%</td>
                    <td className="px-4 py-2 text-xs">{o.certificates}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// ORG COMPLIANCE DASHBOARD (Hospital Admin)
// =========================================================================
const BAND_STYLES = {
  excellent: "bg-emerald-100 text-emerald-800 border-emerald-300",
  good: "bg-sky-100 text-sky-800 border-sky-300",
  needs_improvement: "bg-amber-100 text-amber-800 border-amber-300",
  critical: "bg-rose-100 text-rose-800 border-rose-300",
};

export function OrgComplianceDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: r } = await api.get("/org/reports/compliance-dashboard");
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading || !data) return <DashboardLayout title="Compliance"><LoadingBlock /></DashboardLayout>;
  const t = data.totals;
  return (
    <DashboardLayout title="Compliance Dashboard" breadcrumbs={["Hospital Admin", "Compliance"]}>
      <div className="space-y-6" data-testid="org-compliance-dashboard">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total staff" value={t.total_staff} icon="Users" tone="navy" testId="oc-kpi-staff" index={0} />
          <StatCard label="Assignments" value={t.total_assignments} icon="ClipboardList" tone="slate" testId="oc-kpi-assign" index={1} />
          <StatCard label="Completed" value={t.completed} icon="CircleCheckBig" tone="green" testId="oc-kpi-completed" index={2} />
          <StatCard label="Overdue" value={t.overdue} icon="AlertTriangle" tone="rose" testId="oc-kpi-overdue" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Mandatory total" value={t.mandatory_total} icon="ClipboardCheck" tone="navy" testId="oc-kpi-mandtotal" index={4} />
          <StatCard label="Mandatory done" value={t.mandatory_completed} icon="ShieldCheck" tone="green" testId="oc-kpi-manddone" index={5} />
          <StatCard label="Certificates" value={t.certificates_issued} icon="Award" tone="navy" testId="oc-kpi-certs" index={6} />
          <StatCard label="Renewal due" value={t.renewal_due} icon="RefreshCw" tone="amber" testId="oc-kpi-renewal" index={7} />
        </section>
        <div className={`rounded-2xl border p-6 ${BAND_STYLES[t.compliance_band] || "bg-slate-100 border-slate-300"}`} data-testid="compliance-hero">
          <div className="text-xs font-bold uppercase tracking-widest opacity-80">Mandatory training compliance</div>
          <div className="text-5xl font-black mt-2">{t.compliance_percentage}%</div>
          <div className="text-sm mt-1 capitalize">{(t.compliance_band || "").replace(/_/g, " ")}</div>
          {data.lowest_dept && data.lowest_dept.compliance_percentage < 75 && (
            <div className="text-xs mt-3 opacity-90">
              Lowest-compliance department: <b>{data.lowest_dept.department_name}</b> ({data.lowest_dept.compliance_percentage}%)
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="dept-compliance-table">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-bold">Department-wise compliance</div>
            <div className="text-xs text-slate-500">{data.departments.length} departments</div>
          </div>
          {data.departments.length === 0 ? (
            <EmptyState title="No departments" description="Create departments to see compliance breakdown." icon={Layers} />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Department</th>
                  <th className="px-4 py-3 text-left">Staff</th>
                  <th className="px-4 py-3 text-left">Mandatory</th>
                  <th className="px-4 py-3 text-left">Completed</th>
                  <th className="px-4 py-3 text-left">Overdue</th>
                  <th className="px-4 py-3 text-left">Compliance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.departments.map((d) => (
                  <tr key={d.department_id} data-testid={`dept-row-${d.department_id}`}>
                    <td className="px-4 py-2 font-medium">{d.department_name}</td>
                    <td className="px-4 py-2 text-xs">{d.total_staff}</td>
                    <td className="px-4 py-2 text-xs">{d.mandatory_total}</td>
                    <td className="px-4 py-2 text-xs">{d.mandatory_completed}</td>
                    <td className="px-4 py-2 text-xs">{d.overdue}</td>
                    <td className="px-4 py-2 text-xs">
                      <span className={`inline-block rounded-full border px-2 py-0.5 font-bold ${BAND_STYLES[d.compliance_band] || ""}`}>
                        {d.compliance_percentage}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-4">
          <Link to="/app/hospital/reports/training-matrix" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#101876]/40 hover:shadow-md" data-testid="link-training-matrix">
            <Layers className="h-5 w-5 text-[#101876]" />
            <div className="mt-3 font-bold text-sm">Training Matrix</div>
            <div className="text-xs text-slate-500">Staff × Courses grid</div>
          </Link>
          <Link to="/app/hospital/reports/staff-training" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#101876]/40 hover:shadow-md" data-testid="link-staff-training">
            <Users className="h-5 w-5 text-[#101876]" />
            <div className="mt-3 font-bold text-sm">Staff Training</div>
            <div className="text-xs text-slate-500">Per staff × course record</div>
          </Link>
          <Link to="/app/hospital/reports/mandatory-compliance" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#101876]/40 hover:shadow-md" data-testid="link-mand-compliance">
            <ShieldCheck className="h-5 w-5 text-[#101876]" />
            <div className="mt-3 font-bold text-sm">Mandatory Compliance</div>
            <div className="text-xs text-slate-500">Audit-ready</div>
          </Link>
          <Link to="/app/hospital/reports/audit" className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#101876]/40 hover:shadow-md" data-testid="link-audit-reports">
            <FileSpreadsheet className="h-5 w-5 text-[#101876]" />
            <div className="mt-3 font-bold text-sm">Audit Reports</div>
            <div className="text-xs text-slate-500">NABH / NABL / ISO / JCI</div>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// TRAINING MATRIX
// =========================================================================
const STATUS_CLASS = {
  not_assigned: "bg-slate-100 text-slate-400",
  assigned: "bg-slate-100 text-slate-600",
  pending: "bg-slate-100 text-slate-600",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  overdue: "bg-rose-100 text-rose-800",
  expired: "bg-rose-100 text-rose-800",
  failed: "bg-rose-100 text-rose-800",
};

export function TrainingMatrixPage({ scope = "org" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = scope === "admin" ? "/admin/reports/training-matrix" : "/org/reports/training-matrix";
      const { data: r } = await api.get(endpoint);
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [scope]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = () => {
    if (scope === "org") window.open(`${BACKEND}/api/org/reports/training-matrix/export`, "_blank");
    else toast.info("Export coming soon for global matrix.");
  };
  if (loading || !data) return <DashboardLayout title="Training Matrix"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Training Matrix" breadcrumbs={[scope === "admin" ? "Super Admin" : "Hospital Admin", "Reports", "Training Matrix"]}>
      <div className="space-y-4" data-testid="training-matrix">
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500">{data.staff.length} staff × {data.courses.length} courses</div>
          <button onClick={exportCsv} className="ml-auto rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1" data-testid="btn-export-matrix">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
        {data.staff.length === 0 ? (
          <EmptyState title="No staff yet" description="Add staff and course assignments to see the training matrix." icon={Layers} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto max-h-[70vh]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50/60 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-3 text-left sticky left-0 bg-slate-50/60 z-20 min-w-[220px]">Staff</th>
                  <th className="px-3 py-3 text-left min-w-[120px]">Department</th>
                  {data.courses.map((c) => (
                    <th key={c.id} className="px-2 py-3 text-left min-w-[110px]" title={c.course_title}>
                      <div className="font-mono text-[10px]">{c.course_code}</div>
                      <div className="text-[10px] text-slate-500 line-clamp-1">{c.course_title}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.staff.map((s) => (
                  <tr key={s.user_id} className="hover:bg-slate-50/60" data-testid={`matrix-row-${s.user_id}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      <div className="font-medium">{s.full_name}</div>
                      <div className="text-[10px] text-slate-500">{s.email}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{s.department || "—"}</td>
                    {data.courses.map((c) => {
                      const cell = s.cells[c.id] || { status: "not_assigned" };
                      return (
                        <td key={c.id} className="px-1.5 py-2">
                          <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${STATUS_CLASS[cell.status] || "bg-slate-100"}`}>
                            {cell.status.replace(/_/g, " ")}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// GENERIC TABLE REPORT
// =========================================================================
function GenericReportPage({ title, breadcrumbs, endpoint, columns, testId, exportEndpoint, extraFilters }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, { params: filters });
      setRows(data.data.rows || []);
      if (data.data.summary) setSummary(data.data.summary);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [endpoint, filters]);
  useEffect(() => { load(); }, [load]);
  return (
    <DashboardLayout title={title} breadcrumbs={breadcrumbs}>
      <div className="space-y-4" data-testid={testId}>
        {extraFilters && extraFilters(filters, setFilters)}
        {exportEndpoint && (
          <div className="flex justify-end">
            <button onClick={() => window.open(`${BACKEND}${exportEndpoint}`, "_blank")}
              className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1" data-testid={`${testId}-export`}>
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
          </div>
        )}
        {summary && (
          <div className="grid gap-3 sm:grid-cols-4">
            {Object.entries(summary).map(([k, v]) => (
              <div key={k} className="rounded-md border border-slate-200 bg-white p-3">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{k.replace(/_/g, " ")}</div>
                <div className="text-2xl font-bold mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No data" description="No records match your filters." icon={ClipboardList} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  {columns.map((c) => <th key={c.key} className="px-4 py-3 text-left">{c.label}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={r.id || `${r.user_id || ""}-${r.course_id || ""}-${i}`} className="hover:bg-slate-50/60">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-2 text-xs">
                        {c.render ? c.render(r) : (r[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export function StaffTrainingReportPage() {
  return <GenericReportPage
    title="Staff Training Report"
    breadcrumbs={["Hospital Admin", "Reports", "Staff Training"]}
    endpoint="/org/reports/staff-training"
    testId="staff-training-report"
    exportEndpoint="/api/org/reports/staff-training/export"
    columns={[
      { key: "employee_id", label: "Employee" },
      { key: "learner_name", label: "Name", render: (r) => <div><div className="font-medium">{r.full_name || r.learner_name}</div><div className="text-[10px] text-slate-500">{r.email}</div></div> },
      { key: "department", label: "Dept" },
      { key: "course_code", label: "Course", render: (r) => <div><div className="font-mono">{r.course_code}</div><div className="text-[10px] text-slate-500 line-clamp-1">{r.course_title}</div></div> },
      { key: "is_mandatory", label: "Mandatory", render: (r) => r.is_mandatory ? "Yes" : "No" },
      { key: "due_date", label: "Due", render: (r) => r.due_date?.slice(0, 10) || "—" },
      { key: "progress_percentage", label: "Progress %", render: (r) => `${r.progress_percentage || 0}%` },
      { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      { key: "exam_score", label: "Exam %", render: (r) => r.exam_score != null ? `${r.exam_score}%` : "—" },
      { key: "certificate_status", label: "Certificate", render: (r) => r.certificate_number ? <span className="font-mono text-[10px]">{r.certificate_number}</span> : "—" },
    ]}
  />;
}

export function CourseCompletionReportPage() {
  return <GenericReportPage
    title="Course Completion Report"
    breadcrumbs={["Hospital Admin", "Reports", "Course Completion"]}
    endpoint="/org/reports/course-completion"
    testId="course-completion-report"
    columns={[
      { key: "course_code", label: "Code" },
      { key: "course_title", label: "Course" },
      { key: "total_assigned", label: "Assigned" },
      { key: "completed", label: "Completed" },
      { key: "in_progress", label: "In progress" },
      { key: "not_started", label: "Not started" },
      { key: "overdue", label: "Overdue" },
      { key: "completion_percentage", label: "Completion %", render: (r) => <b>{r.completion_percentage}%</b> },
      { key: "certificates_issued", label: "Certificates" },
    ]}
  />;
}

export function ExamResultsReportPage() {
  return <GenericReportPage
    title="Exam Results Report"
    breadcrumbs={["Hospital Admin", "Reports", "Exam Results"]}
    endpoint="/org/reports/exam-results"
    testId="exam-results-report"
    columns={[
      { key: "employee_id", label: "Employee" },
      { key: "learner_name", label: "Learner" },
      { key: "exam_code", label: "Exam", render: (r) => <div><div className="font-mono">{r.exam_code}</div><div className="text-[10px] text-slate-500 line-clamp-1">{r.exam_title}</div></div> },
      { key: "attempt_number", label: "#" },
      { key: "percentage", label: "Score", render: (r) => `${r.percentage}%` },
      { key: "pass_fail_status", label: "Result", render: (r) => <StatusBadge status={r.pass_fail_status || r.status} /> },
      { key: "submitted_at", label: "Submitted", render: (r) => r.submitted_at?.slice(0, 16).replace("T", " ") },
    ]}
  />;
}

export function CertRegisterReportPage() {
  return <GenericReportPage
    title="Certificate Register"
    breadcrumbs={["Hospital Admin", "Reports", "Certificates"]}
    endpoint="/org/reports/certificate-register"
    testId="cert-register-report"
    columns={[
      { key: "certificate_number", label: "Cert #", render: (r) => <span className="font-mono text-[10px]">{r.certificate_number}</span> },
      { key: "learner_name", label: "Learner" },
      { key: "employee_id", label: "EMP ID" },
      { key: "course_title", label: "Course" },
      { key: "issue_date", label: "Issued", render: (r) => r.issue_date?.slice(0, 10) },
      { key: "expiry_date", label: "Expires", render: (r) => r.expiry_date?.slice(0, 10) || "No expiry" },
      { key: "certificate_status", label: "Status", render: (r) => <StatusBadge status={r.certificate_status} /> },
    ]}
  />;
}

export function CertExpiryReportPage() {
  const [days, setDays] = useState(90);
  return <GenericReportPage
    title="Certificate Expiry Report"
    breadcrumbs={["Hospital Admin", "Reports", "Expiry"]}
    endpoint={`/org/reports/certificate-expiry?days=${days}`}
    testId="cert-expiry-report"
    // eslint-disable-next-line react/no-unstable-nested-components
    extraFilters={() => (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
        <span className="text-xs text-slate-500">Expiring within</span>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border border-slate-200 px-3 py-2 text-sm">
          <option value="7">7 days</option>
          <option value="30">30 days</option>
          <option value="60">60 days</option>
          <option value="90">90 days</option>
          <option value="180">180 days</option>
        </select>
      </div>
    )}
    columns={[
      { key: "certificate_number", label: "Cert #", render: (r) => <span className="font-mono text-[10px]">{r.certificate_number}</span> },
      { key: "learner_name", label: "Learner" },
      { key: "course_title", label: "Course" },
      { key: "expiry_date", label: "Expiry", render: (r) => r.expiry_date?.slice(0, 10) },
      { key: "days_remaining", label: "Days left", render: (r) => <b className={(r.days_remaining ?? 0) < 30 ? "text-rose-600" : "text-slate-800"}>{r.days_remaining ?? "—"}</b> },
      { key: "certificate_status", label: "Status", render: (r) => <StatusBadge status={r.certificate_status} /> },
    ]}
  />;
}

export function MandatoryComplianceReportPage() {
  return <GenericReportPage
    title="Mandatory Training Compliance"
    breadcrumbs={["Hospital Admin", "Reports", "Mandatory Compliance"]}
    endpoint="/org/reports/mandatory-compliance"
    testId="mandatory-compliance-report"
    columns={[
      { key: "employee_id", label: "Employee" },
      { key: "learner_name", label: "Staff" },
      { key: "department", label: "Dept" },
      { key: "course_code", label: "Course", render: (r) => <div><div className="font-mono">{r.course_code}</div><div className="text-[10px] text-slate-500 line-clamp-1">{r.course_title}</div></div> },
      { key: "due_date", label: "Due", render: (r) => r.due_date?.slice(0, 10) || "—" },
      { key: "status", label: "Course", render: (r) => <StatusBadge status={r.status} /> },
      { key: "overdue_days", label: "Overdue", render: (r) => (r.overdue_days > 0 ? <span className="text-rose-600 font-bold">{r.overdue_days}d</span> : "—") },
      { key: "compliance_status", label: "Compliance", render: (r) => <StatusBadge status={r.compliance_status} /> },
    ]}
  />;
}

// =========================================================================
// AUDIT REPORTS SELECTOR
// =========================================================================
export function AuditReportsPage() {
  const [type, setType] = useState("infection-control");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get(`/org/reports/audit/${type}`);
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [type]);
  useEffect(() => { load(); }, [load]);
  return (
    <DashboardLayout title="Audit-Ready Reports" breadcrumbs={["Hospital Admin", "Reports", "Audit"]}>
      <div className="space-y-4" data-testid="audit-reports">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <label className="text-xs text-slate-500 font-bold uppercase tracking-widest">Report</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-72" data-testid="audit-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nabh">NABH Training Compliance</SelectItem>
              <SelectItem value="nabl">NABL Training Compliance</SelectItem>
              <SelectItem value="iso">ISO Training Compliance</SelectItem>
              <SelectItem value="jci">JCI Training Compliance</SelectItem>
              <SelectItem value="fire-safety">Fire Safety Training</SelectItem>
              <SelectItem value="infection-control">Infection Control</SelectItem>
              <SelectItem value="biomedical-waste">Biomedical Waste</SelectItem>
              <SelectItem value="internal">Internal Audit</SelectItem>
            </SelectContent>
          </Select>
          {data?.summary && (
            <div className="ml-auto text-xs text-slate-500">
              Compliance: <b className="text-slate-800">{data.summary.compliance_percentage}%</b> · {data.summary.completed}/{data.summary.total_records}
            </div>
          )}
        </div>
        {loading || !data ? <LoadingBlock /> : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <div className="text-lg font-bold">{data.audit_type.toUpperCase().replace(/-/g, " ")} — {data.organization_name}</div>
              <div className="text-xs text-slate-500">Generated by {data.generated_by} · {data.generated_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
            {data.rows.length === 0 ? (
              <EmptyState title="No matching records" description="No courses match this audit category, or none have been assigned." icon={FileSpreadsheet} />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Employee</th>
                    <th className="px-4 py-3 text-left">Staff</th>
                    <th className="px-4 py-3 text-left">Department</th>
                    <th className="px-4 py-3 text-left">Course</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Certificate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.rows.slice(0, 500).map((r, i) => (
                    <tr key={`${r.user_id}-${r.course_id}-${i}`}>
                      <td className="px-4 py-2 text-xs">{r.employee_id}</td>
                      <td className="px-4 py-2 text-xs">{r.learner_name}<div className="text-[10px] text-slate-500">{r.email}</div></td>
                      <td className="px-4 py-2 text-xs">{r.department || "—"}</td>
                      <td className="px-4 py-2 text-xs font-mono">{r.course_code}</td>
                      <td className="px-4 py-2 text-xs"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-2 text-xs">{r.certificate_number || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="p-4 border-t border-slate-100 text-center text-[10px] text-slate-500">
              Generated via Steward Learning Cloud · SIHMT · This report is suitable for audit submission.
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// LEARNER TRANSCRIPT
// =========================================================================
export function LearnerTranscriptPage() {
  const [data, setData] = useState(null);
  const [totals, setTotals] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [t, d] = await Promise.all([
          api.get("/learner/reports/transcript"),
          api.get("/learner/reports/dashboard"),
        ]);
        setData(t.data.data);
        setTotals(d.data.data.totals);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const downloadTranscript = () => {
    if (!data) return;
    const header = ["Course Code", "Course Title", "Type", "Progress %", "Status", "Exam Result", "Exam Score", "Certificate Number"];
    const rows = data.transcript.map((t) => [
      t.course_code || "",
      t.course_title || "",
      t.is_mandatory ? "Mandatory" : "Optional",
      t.progress_percentage ?? 0,
      t.status || "",
      t.exam_result || "",
      t.exam_score ?? "",
      t.certificate_number || "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transcript_${data.user.employee_id || data.user.email || "learner"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  if (!data || !totals) return <DashboardLayout title="My Transcript"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="My Learning Transcript" breadcrumbs={["My Learning", "Transcript"]}>
      <div className="space-y-4" data-testid="learner-transcript">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={downloadTranscript}
            data-testid="transcript-download-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#0b1258] transition-colors"
          >
            Download Transcript
          </button>
        </div>
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Assigned" value={totals.total_assigned} icon="ClipboardList" tone="navy" testId="lt-kpi-assigned" index={0} />
          <StatCard label="Completed" value={totals.completed} icon="CircleCheckBig" tone="green" testId="lt-kpi-completed" index={1} />
          <StatCard label="Exams passed" value={totals.exams_passed} icon="FileCheck" tone="green" testId="lt-kpi-passed" index={2} />
          <StatCard label="Certificates" value={totals.certificates_valid} icon="Award" tone="navy" testId="lt-kpi-certs" index={3} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Learner</div>
          <div className="text-lg font-bold mt-1">{data.user.full_name}</div>
          <div className="text-xs text-slate-500">{data.user.email} · {data.user.employee_id || "—"}</div>
        </div>
        {data.transcript.length === 0 ? (
          <EmptyState title="No courses yet" description="Once you're assigned courses, they'll appear on your transcript." icon={GraduationCap} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Course</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Progress</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Exam</th>
                  <th className="px-4 py-3 text-left">Certificate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.transcript.map((t, i) => (
                  <tr key={i} data-testid={`transcript-row-${i}`}>
                    <td className="px-4 py-2"><div className="font-medium">{t.course_title}</div><div className="font-mono text-[10px] text-slate-500">{t.course_code}</div></td>
                    <td className="px-4 py-2 text-xs">{t.is_mandatory ? "Mandatory" : "Optional"}</td>
                    <td className="px-4 py-2 text-xs">{t.progress_percentage}%</td>
                    <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-2 text-xs">{t.exam_result ? <><StatusBadge status={t.exam_result} /> {t.exam_score}%</> : "—"}</td>
                    <td className="px-4 py-2 text-xs">{t.certificate_number ? <span className="font-mono text-[10px]">{t.certificate_number}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// FACULTY ANALYTICS
// =========================================================================
export function FacultyAnalyticsDashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/faculty/reports/dashboard");
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!data) return <DashboardLayout title="Faculty Analytics"><LoadingBlock /></DashboardLayout>;
  const t = data.totals;
  return (
    <DashboardLayout title="Faculty Analytics" breadcrumbs={["Faculty", "Analytics"]}>
      <div className="space-y-6" data-testid="faculty-analytics">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Assigned courses" value={t.assigned_courses} icon="GraduationCap" tone="navy" testId="fa-kpi-courses" index={0} />
          <StatCard label="Learners" value={t.total_learners} icon="Users" tone="green" testId="fa-kpi-learners" index={1} />
          <StatCard label="Completion %" value={`${t.completion_percentage}%`} icon="TrendingUp" tone="navy" testId="fa-kpi-completion" index={2} />
          <StatCard label="Exam attempts" value={t.exam_attempts} icon="FileCheck" tone="slate" testId="fa-kpi-attempts" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Pass %" value={`${t.pass_percentage}%`} icon="CircleCheckBig" tone="green" testId="fa-kpi-pass" index={4} />
          <StatCard label="Pending evaluations" value={t.pending_evaluations} icon="ClipboardCheck" tone="amber" testId="fa-kpi-pending" index={5} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-bold">My courses</div>
          </div>
          {data.courses.length === 0 ? (
            <EmptyState title="No assigned courses" description="You have no courses assigned as trainer yet." icon={GraduationCap} />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Code</th><th className="px-4 py-3 text-left">Course</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.courses.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-2 font-mono text-xs">{c.course_code}</td>
                    <td className="px-4 py-2 text-xs">{c.course_title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
